import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { AbstractToken } from '@/hooks/useFirestoreCrypto';

export interface TokenMetadata {
  symbol: string;
  name: string;
  address: string;
  featured: boolean;
  priority: number;
  description?: string;
  customImage?: string;
  
  // Social Links
  socialLinks?: {
    website?: string;
    twitter?: string;
    telegram?: string;
    discord?: string;
    whitepaper?: string;
  };
  
  // Custom Links (admin-defined)
  customLinks?: Array<{
    label: string;
    url: string;
  }>;
  
  updatedAt: string;
  updatedBy: string;
}

export class TokenManagementService {
  private get tokensCollection() {
    if (!db) throw new Error('Firebase not initialized');
    return collection(db, 'token_metadata');
  }

  // Get all token metadata
  async getTokenMetadata(): Promise<TokenMetadata[]> {
    if (!db) throw new Error('Firebase not initialized');
    
    const q = query(this.tokensCollection, orderBy('priority', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as TokenMetadata & { id: string }));
  }

  // Get metadata for a specific token by symbol
  async getTokenMetadataBySymbol(symbol: string): Promise<TokenMetadata | null> {
    if (!db) throw new Error('Firebase not initialized');
    
    const tokenDoc = doc(this.tokensCollection, symbol.toUpperCase());
    const snapshot = await getDocs(collection(db, 'token_metadata'));
    
    const tokenData = snapshot.docs.find(doc => doc.id === symbol.toUpperCase());
    return tokenData ? { id: tokenData.id, ...tokenData.data() } as TokenMetadata & { id: string } : null;
  }

  // Update token metadata
  async updateTokenMetadata(
    symbol: string, 
    metadata: Partial<TokenMetadata>, 
    userId: string
  ): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');
    
    const tokenDoc = doc(this.tokensCollection, symbol.toUpperCase());
    
    await setDoc(tokenDoc, {
      ...metadata,
      symbol: symbol.toUpperCase(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    }, { merge: true });
  }

  // Create new token metadata
  async createTokenMetadata(
    tokenData: Omit<TokenMetadata, 'updatedAt' | 'updatedBy'>,
    userId: string
  ): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');
    
    const tokenDoc = doc(this.tokensCollection, tokenData.symbol.toUpperCase());
    
    await setDoc(tokenDoc, {
      ...tokenData,
      symbol: tokenData.symbol.toUpperCase(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  }

  // Delete token metadata
  async deleteTokenMetadata(symbol: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');
    
    const tokenDoc = doc(this.tokensCollection, symbol.toUpperCase());
    await deleteDoc(tokenDoc);
  }

  // Get featured tokens only
  async getFeaturedTokens(): Promise<TokenMetadata[]> {
    const allTokens = await this.getTokenMetadata();
    return allTokens.filter(token => token.featured);
  }

  // Update token priority
  async updateTokenPriority(symbol: string, priority: number, userId: string): Promise<void> {
    await this.updateTokenMetadata(symbol, { priority }, userId);
  }

  // Toggle featured status
  async toggleFeatured(symbol: string, featured: boolean, userId: string): Promise<void> {
    if (!symbol || !userId) {
      throw new Error('Symbol and userId are required');
    }
    await this.updateTokenMetadata(symbol, { featured }, userId);
  }

  // Batch update multiple tokens
  async batchUpdateTokens(
    updates: Array<{ symbol: string; metadata: Partial<TokenMetadata> }>,
    userId: string
  ): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');
    
    const batch = updates.map(({ symbol, metadata }) => 
      this.updateTokenMetadata(symbol, metadata, userId)
    );
    
    await Promise.all(batch);
  }

  // Get token statistics
  async getTokenStats(): Promise<{
    total: number;
    featured: number;
    withCustomImages: number;
    averagePriority: number;
  }> {
    const metadata = await this.getTokenMetadata();
    
    return {
      total: metadata.length,
      featured: metadata.filter(token => token.featured).length,
      withCustomImages: metadata.filter(token => token.customImage).length,
      averagePriority: metadata.length > 0 
        ? metadata.reduce((sum, token) => sum + token.priority, 0) / metadata.length 
        : 0
    };
  }
}
